import React from 'react';
import {
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import { useTheme } from '@material-ui/core';

interface AdvancedAlertProps {
  children?: React.ReactNode;
  title?: string;
  subtitle?: string;
  severity?: 'info' | 'warning' | 'error' | 'success';
  showIcon?: boolean;
}

const AdvancedAlert = ({
  children,
  title,
  subtitle,
  severity = 'warning',
  showIcon = true,
}: AdvancedAlertProps) => {
  const theme = useTheme();

  const getColors = () => {
    switch (severity) {
      case 'error':
        return {
          backgroundColor: theme.palette.error?.light,
          borderColor: theme.palette.error?.main,
          textColor: theme.palette.error?.dark,
          iconColor: theme.palette.error?.main,
        };
      case 'success':
        return {
          backgroundColor: theme.palette.success?.light,
          borderColor: theme.palette.success?.main,
          textColor: theme.palette.success?.dark,
          iconColor: theme.palette.success?.main,
        };
      case 'info':
        return {
          backgroundColor: theme.palette.info?.light,
          borderColor: theme.palette.info?.main,
          textColor: theme.palette.info?.dark,
          iconColor: theme.palette.info?.main,
        };
      case 'warning':
      default:
        return {
          backgroundColor: theme.palette.warning?.light,
          borderColor: theme.palette.warning?.main,
          textColor: theme.palette.warning?.dark,
          iconColor: theme.palette.warning?.main,
        };
    }
  };

  const colors = getColors();

  const getIcon = () => {
    switch (severity) {
      case 'error':
        return <CloseCircleOutlined />;
      case 'success':
        return <CheckCircleOutlined />;
      case 'info':
        return <InfoCircleOutlined />;
      case 'warning':
      default:
        return <ExclamationCircleOutlined />;
    }
  };

  return (
    <div
      className={`advanced-alert advanced-alert--${severity}`}
      style={{
        backgroundColor: colors.backgroundColor,
        borderLeft: `4px solid ${colors.borderColor}`,
        color: colors.textColor,
      }}
    >
      {showIcon ? (
        <div
          className="advanced-alert__icon"
          style={{ color: colors.iconColor }}
        >
          {getIcon()}
        </div>
      ) : null}

      <div className="advanced-alert__content">
        {title ? (
          <div className="advanced-alert__title">
            <strong>{title}</strong>
          </div>
        ) : null}

        {subtitle ? (
          <div className="advanced-alert__subtitle">{subtitle}</div>
        ) : null}

        {children ? (
          <div className="advanced-alert__body">{children}</div>
        ) : null}
      </div>
    </div>
  );
};

export default AdvancedAlert;
